import { useMemo } from 'react';

import {
  BoardFertilization,
  BoardMineralization,
  BoardSoil,
  BoardStatus,
} from 'features/Croptimizer/types';
import { gainsOmhelper } from 'features/Croptimizer/utils/calcHelperBoard';
import { cmToFineDry, numericValues, unMaskInput } from 'utils/helpers';

interface Props {
  boardStatusValues: BoardStatus | null;
  mineralizationValues: BoardMineralization | null;
  boardSoilValues: BoardSoil | null;
  fertilizationValues: BoardFertilization | null;
  getY1CurrentProfit?: string | number;
  getY2CurrentProfit?: string | number;
  getY3CurrentProfit?: string | number;
  getY4CurrentProfit?: string | number;
  getY5CurrentProfit?: string | number;
  getY1CurrentLoss: string;
  getY2CurrentLoss: string;
  getY3CurrentLoss: string;
  getY4CurrentLoss: string;
  getY5CurrentLoss: string;
}

const useGainsOm = ({
  boardStatusValues,
  mineralizationValues,
  boardSoilValues,
  fertilizationValues,
  getY1CurrentLoss,
  getY2CurrentLoss,
  getY3CurrentLoss,
  getY4CurrentLoss,
  getY5CurrentLoss,
  getY1CurrentProfit,
  getY2CurrentProfit,
  getY3CurrentProfit,
  getY4CurrentProfit,
  getY5CurrentProfit,
}: Props) => {
  const useGainsOm1 = useMemo(() => {
    if (
      boardStatusValues &&
      mineralizationValues &&
      boardSoilValues &&
      fertilizationValues
    ) {
      const fineAndDry = cmToFineDry(Number(unMaskInput(boardSoilValues?.fineDrySoil))); 
      const OM =
        Number(numericValues(mineralizationValues?.organicMatter)) / 100;
      const N_rate = mineralizationValues?.nRate;
      const P2O5_rate = mineralizationValues?.pRate;
      const SO3_rate = mineralizationValues?.sRate;
      const Soil_ph_achieved_Y1 = Number(getY1CurrentProfit);
      const Soil_Ph_evolution_Y1 = Number(getY1CurrentLoss);
      const N_from_NPK_Prices = Number(
        unMaskInput(fertilizationValues?.nprice),
      );
      const P2O5_from_NPK_Prices = Number(
        unMaskInput(fertilizationValues?.pprice),
      );
      const SO3_price = Number(unMaskInput(mineralizationValues?.sPrice));

      return gainsOmhelper(
        fineAndDry,
        OM,
        N_rate,
        P2O5_rate,
        SO3_rate,
        Soil_ph_achieved_Y1,
        Soil_Ph_evolution_Y1,
        N_from_NPK_Prices,
        P2O5_from_NPK_Prices,
        SO3_price,
      );
    }

    return '';
  }, [
    boardStatusValues,
    mineralizationValues,
    getY1CurrentLoss,
    getY1CurrentProfit,
  ]);

  const useGainsOm2 = useMemo(() => {
    if (
      boardStatusValues &&
      mineralizationValues &&
      boardSoilValues &&
      fertilizationValues
    ) {
      const fineAndDry = cmToFineDry(Number(unMaskInput(boardSoilValues?.fineDrySoil))); 
      const OM =
        Number(numericValues(mineralizationValues?.organicMatter)) / 100;
      const N_rate = mineralizationValues?.nRate;
      const P2O5_rate = mineralizationValues?.pRate;
      const SO3_rate = mineralizationValues?.sRate;
      const Soil_ph_achieved_Y1 = Number(getY2CurrentProfit);
      const Soil_Ph_evolution_Y1 = Number(getY2CurrentLoss);
      const N_from_NPK_Prices = Number(
        unMaskInput(fertilizationValues?.nprice),
      );
      const P2O5_from_NPK_Prices = Number(
        unMaskInput(fertilizationValues?.pprice),
      );
      const SO3_price = Number(unMaskInput(mineralizationValues?.sPrice));

      return gainsOmhelper(
        fineAndDry,
        OM,
        N_rate,
        P2O5_rate,
        SO3_rate,
        Soil_ph_achieved_Y1,
        Soil_Ph_evolution_Y1,
        N_from_NPK_Prices,
        P2O5_from_NPK_Prices,
        SO3_price,
      );
    }

    return '';
  }, [
    boardStatusValues,
    mineralizationValues,
    getY2CurrentProfit,
    getY2CurrentLoss,
  ]);

  const useGainsOm3 = useMemo(() => {
    if (
      boardStatusValues &&
      mineralizationValues &&
      boardSoilValues &&
      fertilizationValues
    ) {
      const fineAndDry = cmToFineDry(Number(unMaskInput(boardSoilValues?.fineDrySoil))); 
      const OM =
        Number(numericValues(mineralizationValues?.organicMatter)) / 100;
      const N_rate = mineralizationValues?.nRate;
      const P2O5_rate = mineralizationValues?.pRate;
      const SO3_rate = mineralizationValues?.sRate;
      const Soil_ph_achieved_Y1 = Number(getY3CurrentProfit);
      const Soil_Ph_evolution_Y1 = Number(getY3CurrentLoss);
      const N_from_NPK_Prices = Number(
        unMaskInput(fertilizationValues?.nprice),
      );
      const P2O5_from_NPK_Prices = Number(
        unMaskInput(fertilizationValues?.pprice),
      );
      const SO3_price = Number(unMaskInput(mineralizationValues?.sPrice));

      return gainsOmhelper(
        fineAndDry,
        OM,
        N_rate,
        P2O5_rate,
        SO3_rate,
        Soil_ph_achieved_Y1,
        Soil_Ph_evolution_Y1,
        N_from_NPK_Prices,
        P2O5_from_NPK_Prices,
        SO3_price,
      );
    }

    return '';
  }, [
    boardStatusValues,
    mineralizationValues,
    getY3CurrentProfit,
    getY3CurrentLoss,
  ]);

  const useGainsOm4 = useMemo(() => {
    if (
      boardStatusValues &&
      mineralizationValues &&
      boardSoilValues &&
      fertilizationValues
    ) {
      const fineAndDry = cmToFineDry(Number(unMaskInput(boardSoilValues?.fineDrySoil))); 
      const OM =
        Number(numericValues(mineralizationValues?.organicMatter)) / 100;
      const N_rate = mineralizationValues?.nRate;
      const P2O5_rate = mineralizationValues?.pRate;
      const SO3_rate = mineralizationValues?.sRate;
      const Soil_ph_achieved_Y1 = Number(getY4CurrentProfit);
      const Soil_Ph_evolution_Y1 = Number(getY4CurrentLoss);
      const N_from_NPK_Prices = Number(
        unMaskInput(fertilizationValues?.nprice),
      );
      const P2O5_from_NPK_Prices = Number(
        unMaskInput(fertilizationValues?.pprice),
      );
      const SO3_price = Number(unMaskInput(mineralizationValues?.sPrice));

      return gainsOmhelper(
        fineAndDry,
        OM,
        N_rate,
        P2O5_rate,
        SO3_rate,
        Soil_ph_achieved_Y1,
        Soil_Ph_evolution_Y1,
        N_from_NPK_Prices,
        P2O5_from_NPK_Prices,
        SO3_price,
      );
    }

    return '';
  }, [
    boardStatusValues,
    mineralizationValues,
    getY4CurrentProfit,
    getY4CurrentLoss,
  ]);

  const useGainsOm5 = useMemo(() => {
    if (
      boardStatusValues &&
      mineralizationValues &&
      boardSoilValues &&
      fertilizationValues
    ) {
      const fineAndDry = cmToFineDry(Number(unMaskInput(boardSoilValues?.fineDrySoil))); 
      const OM =
        Number(numericValues(mineralizationValues?.organicMatter)) / 100;
      const N_rate = mineralizationValues?.nRate;
      const P2O5_rate = mineralizationValues?.pRate;
      const SO3_rate = mineralizationValues?.sRate;
      const Soil_ph_achieved_Y1 = Number(getY5CurrentProfit);
      const Soil_Ph_evolution_Y1 = Number(getY5CurrentLoss);
      const N_from_NPK_Prices = Number(
        unMaskInput(fertilizationValues?.nprice),
      );
      const P2O5_from_NPK_Prices = Number(
        unMaskInput(fertilizationValues?.pprice),
      );
      const SO3_price = Number(unMaskInput(mineralizationValues?.sPrice));

      return gainsOmhelper(
        fineAndDry,
        OM,
        N_rate,
        P2O5_rate,
        SO3_rate,
        Soil_ph_achieved_Y1,
        Soil_Ph_evolution_Y1,
        N_from_NPK_Prices,
        P2O5_from_NPK_Prices,
        SO3_price,
      );
    }

    return '';
  }, [
    boardStatusValues,
    mineralizationValues,
    getY5CurrentProfit,
    getY5CurrentLoss,
  ]);

  const totalOmGains = useMemo(
    () => Number(useGainsOm1) +
      Number(useGainsOm2) +
      Number(useGainsOm3) +
      Number(useGainsOm4) +
      Number(useGainsOm5),
    [useGainsOm1, useGainsOm2, useGainsOm3, useGainsOm4, useGainsOm5],
  );

  return {
    useGainsOm1,
    useGainsOm2,
    useGainsOm3,
    useGainsOm4,
    useGainsOm5,
    totalOmGains,
  };
};

export default useGainsOm;
